import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Award, Globe, ShieldCheck } from "lucide-react";
import PageHero from "../components/PageHero";
import Seo from "../components/Seo";
import { Reveal, SectionHeading } from "../components/Common";
import { IMAGES, MILESTONES } from "../mock/mock";

const ADVANTAGES = [
  { icon: ShieldCheck, t: "Policy Influence", d: "Supportive government initiatives and active engagement with the Agarwood Promotion Policy, 2020." },
  { icon: Globe, t: "Export & Trade", d: "Export–import licence and trade memberships spanning 6 GCC nations and parts of Europe." },
  { icon: Award, t: "Toward CITES", d: "On its way to becoming the first agarwood plantation company to receive CITES export certification." },
];

const Recognitions = () => {
  return (
    <div className="bg-noir">
      <Seo
        title="Recognitions & Milestones — Awards & CITES | Meilleur Oud"
        description="Meilleur on the global stage — Perfume & Oud Exhibition Sharjah, ICC India–UAE Business Forum, and progress toward CITES export certification for agarwood."
        path="/recognitions"
        keywords="oud CITES certification, agarwood exhibition, Meilleur recognitions, oud trade fair, agarwood awards India"
      />
      <PageHero eyebrow="On the Global Stage" title="Recognition &" accent="Milestones" image={IMAGES.perfumeGold} crumbs={["Recognitions"]} />

      {/* Advantage */}
      <section className="py-28">
        <div className="max-w-[1360px] mx-auto px-6 md:px-10">
          <Reveal><SectionHeading eyebrow="What Sets Us Apart" title="Competitive" accent="advantage" align="center" /></Reveal>
          <div className="mt-16 grid md:grid-cols-3 gap-7">
            {ADVANTAGES.map((a, i) => (
              <Reveal key={a.t} delay={i * 110}>
                <div className="lux-card p-10 h-full">
                  <a.icon size={30} className="text-[var(--gold)] mb-7" />
                  <h3 className="font-serif-display text-2xl mb-3">{a.t}</h3>
                  <p className="text-sm text-[var(--ivory-muted)] font-body leading-relaxed">{a.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-24 bg-noir-2 border-y border-[var(--line)]">
        <div className="max-w-[1360px] mx-auto px-6 md:px-10">
          <Reveal><SectionHeading eyebrow="Recent Milestones" title="Representing India," accent="abroad" /></Reveal>
          <div className="mt-16 relative">
            <div className="absolute left-0 md:left-[120px] top-0 bottom-0 w-px bg-[var(--line)]" />
            <div className="space-y-2">
              {MILESTONES.map((m, i) => (
                <Reveal key={m.title} delay={i * 90}>
                  <div className="relative grid md:grid-cols-12 gap-6 items-center py-7 pl-8 md:pl-0 group">
                    <div className="md:col-span-2 md:text-right md:pr-10">
                      <span className="font-serif-display text-3xl gold-text">{m.year}</span>
                    </div>
                    <span className="absolute left-[-5px] md:left-[115px] w-2.5 h-2.5 rounded-full bg-[var(--gold)] group-hover:scale-150 transition-transform" />
                    <div className="md:col-span-10 md:pl-12">
                      <h3 className="font-serif-display text-2xl group-hover:text-[var(--gold-light)] transition-colors">{m.title}</h3>
                      <p className="text-[var(--ivory-muted)] font-body mt-1">{m.place}</p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Leadership note */}
      <section className="py-28">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <Reveal>
            <p className="font-serif-display text-2xl md:text-[2.1rem] leading-[1.4] italic">
              The Chairman of Meilleur Plantations has been appointed <span className="gold-text">Chairman, Indian
              Chamber of Commerce — Tripura Region</span>, and has represented the company at Invest India's round-table
              interactions in New Delhi.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="pb-32 text-center">
        <Reveal>
          <Link to="/contact" className="btn-gold">Partner With Us <ArrowRight size={15} /></Link>
        </Reveal>
      </section>
    </div>
  );
};

export default Recognitions;
